import { Thumb } from "../lib/thumb";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import KpiCard from "@/components/KpiCard";
import MobileKpiRail from "@/components/MobileKpiRail";
import { fmtNumber } from "@/lib/format";
import { useCurrency } from "@/lib/currency";
import { Package, AlertTriangle } from "lucide-react";

export default function Inventory() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || []));
  }, []);

  const { fmtMoney } = useCurrency();
  const totalUnits = products.reduce((s, p) => s + (p.stock || 0), 0);
  const inventoryValue = products.reduce((s, p) => s + (p.cost || 0) * (p.stock || 0), 0);
  const retailValue = products.reduce((s, p) => s + (p.price || 0) * (p.stock || 0), 0);
  const lowStock = products.filter((p) => p.stock <= (p.reorder_point || 10));

  return (
    <div data-testid="inventory-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Retail inventory"
        subtitle="Live product stock levels and reorder alerts"
        testid="inventory-header"
      />

      <MobileKpiRail className="mb-6">
         <KpiCard testid="inv-skus" label="SKUs" value={fmtNumber(products.length)} sub="retail products" />
        <KpiCard testid="inv-units" label="Total units" value={fmtNumber(totalUnits)} sub="on hand" />
        <KpiCard testid="inv-cost" label="Inventory value" value={fmtMoney(inventoryValue)} sub="at cost" />
        <KpiCard testid="inv-retail" label="Retail value" value={fmtMoney(retailValue)} sub="if sold at MSRP" />
      </MobileKpiRail>

      {lowStock.length > 0 && (
        <Card testid="low-stock-list" className="mb-6" title={<><AlertTriangle className="inline w-4 h-4 text-[#FF9500] mr-1.5 -mt-0.5" /> Reorder needed</>} subtitle={`${lowStock.length} products at or below reorder point`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {lowStock.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-2xl bg-[#FFF8E6] border border-[#FF9500]/20">
                {p.image_url ? (
                  <Thumb src={p.image_url} alt={p.name} className="w-11 h-11 rounded-lg object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-lg bg-black/5 flex items-center justify-center"><Package className="w-4 h-4 text-[#8E8E93]" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{p.name}</div>
                  <div className="text-[11px] text-[#3C3C43]/70">{p.sku} · reorder at {p.reorder_point}</div>
                </div>
                <div className="text-right">
                  <div className="text-[15px] font-semibold text-[#FF3B30]">{p.stock}</div>
                  <div className="text-[10px] text-[#8E8E93] uppercase tracking-wider">left</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

       <Card title="All retail products" subtitle="Stockroom view">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => {
            const isLow = p.stock <= (p.reorder_point || 10);
            return (
              <div key={p.id} data-testid={`inventory-${p.sku}`} className="p-4 rounded-2xl bg-white border border-black/[0.04] card-hover flex gap-3">
                {p.image_url ? (
                  <Thumb src={p.image_url} alt={p.name} className="w-16 h-16 rounded-xl object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-black/5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium truncate">{p.name}</div>
                  <div className="text-[11px] text-[#8E8E93] font-mono">{p.sku}</div>
                  <div className="mt-2 flex items-center gap-2 text-[11.5px]">
                    <span className={`px-2 py-0.5 rounded-full ${isLow ? "bg-[#FF3B30]/10 text-[#9F1F17]" : "bg-[#34C759]/10 text-[#1B7B36]"}`}>
                      {p.stock} in stock
                    </span>
                    <span className="text-[#8E8E93]">{fmtMoney(p.cost)} cost · {fmtMoney(p.price)} MSRP</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
